import React, { useState, useEffect } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomTextField from "../form/CustomTextField";
import { getCompanyAction, updateCompanyAction } from "../redux/actions/company";
import { createGiftAction, updateGiftAction, getGiftsAction } from "../redux/actions/gift";
import { useDispatch, useSelector } from "react-redux";
import strings from "../i18n/language";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';
const TEAL = '#5bbfb5';

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
};

const sectionTitle = {
  fontSize: 12,
  fontWeight: 600,
  color: MUTED,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  marginBottom: 16,
};


const FieldRow = ({ tooltip, children, hint }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ position: 'relative' }}>
      {children}
      {tooltip && (
        <Tooltip title={tooltip} placement="left">
          <InfoOutlinedIcon style={{ fontSize: 13, color: MUTED, cursor: 'help', position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)' }} />
        </Tooltip>
      )}
    </div>
    {hint}
  </div>
);

const GiftCard = ({ tier, points, label, error, onChange, accent }) => (
  <div style={{
    background: 'rgba(255,255,255,0.03)',
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: '16px 18px',
    position: 'relative',
    overflow: 'hidden',
  }}>
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 2,
      background: `linear-gradient(90deg, ${accent}, transparent)`,
    }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <EmojiEventsRoundedIcon style={{ fontSize: 15, color: accent }} />
      <Typography style={{ fontSize: 13, fontWeight: 600, color: '#e8f0fe' }}>{tier}</Typography>
      <span style={{
        background: `${accent}22`,
        color: accent,
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 5,
        letterSpacing: 0.3,
      }}>
        {points} pts
      </span>
    </div>
    <CustomTextField
      id={`gift-${tier}`}
      label="Etiqueta del regalo"
      value={label}
      onChange={onChange}
      margin="none"
    />
  </div>
);

const Plus = () => {
  const dispatch = useDispatch();
  const [pointExpiration, setPointExpiration] = useState("");
  const [errorPointExpiration, setErrorPointExpiration] = useState(false);
  const [giftExpiration, setGiftExpiration] = useState("");
  const [errorGiftExpiration, setErrorGiftExpiration] = useState(false);
  const [requirement, setRequirement] = useState("");
  const [errorRequirement, setErrorRequirement] = useState(false);
  const [errorBasicGift, setErrorBasicGift] = useState(false);
  const [errorIntermediateGift, setErrorIntermediateGift] = useState(false);
  const [errorAdvancedGift, setErrorAdvancedGift] = useState(false);
  const [valid, setValid] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [originalCompany, setOriginalCompany] = useState({});
  const company = useSelector((state) => state.company.company);
  const user = useSelector((state) => state.user.user);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);
  const gifts = useSelector((state) => state.gift.gifts);

  const [giftBasic, setGiftBasic] = useState({ label: null, description: null, type: "basic", target: 10 });
  const [giftIntermediate, setGiftIntermediate] = useState({ label: null, description: null, type: "intermediate", target: 8 });
  const [giftAdvanced, setGiftAdvanced] = useState({ label: null, description: null, type: "advanced", target: 6 });
  const [originalGiftBasic, setOriginalGiftBasic] = useState({});
  const [originalGiftIntermediate, setOriginalGiftIntermediate] = useState({});
  const [originalGiftAdvanced, setOriginalGiftAdvanced] = useState({});

  useEffect(() => {
    if (gifts.length) {
      gifts.forEach((gift) => {
        if (gift.type === "basic") { setGiftBasic(gift); setOriginalGiftBasic(gift); }
        if (gift.type === "intermediate") { setGiftIntermediate(gift); setOriginalGiftIntermediate(gift); }
        if (gift.type === "advanced") { setGiftAdvanced(gift); setOriginalGiftAdvanced(gift); }
      });
    }
  }, [gifts]);

  useEffect(() => {
    if (selectedCompany) dispatch(getCompanyAction(selectedCompany.id));
  }, [selectedCompany, dispatch]);

  useEffect(() => {
    if (company) {
      setOriginalCompany(company);
      setPointExpiration(company.pointExpiration);
      setGiftExpiration(company.giftExpiration);
      setRequirement(company.requirement);
      dispatch(getGiftsAction(company.id));
    } else if (user && user.company_id && !company) {
      dispatch(getCompanyAction(user.company_id));
    }
  }, [company, user, dispatch]);

  useEffect(() => {
    setErrorRequirement(!/^[a-zA-Z0-9 $.À-ÿ%!¡,+$€():]+$/i.test(requirement));
  }, [requirement]);

  useEffect(() => {
    setErrorPointExpiration(!/^(\$)?(?:[4-9]|\d{2,})$/i.test(pointExpiration));
  }, [pointExpiration]);

  useEffect(() => {
    setErrorGiftExpiration(!/^(?:[4-9]|\d{2,})$/i.test(giftExpiration));
  }, [giftExpiration]);

  useEffect(() => {
    const re = /^[a-zA-Z0-9 $.À-ÿ%!¡,+$€():]+$/i;
    setErrorBasicGift(!giftBasic.label || !re.test(giftBasic.label));
  }, [giftBasic.label]);

  useEffect(() => {
    const re = /^[a-zA-Z0-9 $.À-ÿ%!¡,+$€():]+$/i;
    setErrorIntermediateGift(!giftIntermediate.label || !re.test(giftIntermediate.label));
  }, [giftIntermediate.label]);

  useEffect(() => {
    const re = /^[a-zA-Z0-9 $.À-ÿ%!¡,+$€():]+$/i;
    setErrorAdvancedGift(!giftAdvanced.label || !re.test(giftAdvanced.label));
  }, [giftAdvanced.label]);

  useEffect(() => {
    setValid(!errorPointExpiration && !errorRequirement);
  }, [errorPointExpiration, errorRequirement]);

  useEffect(() => {
    setDirty(
      originalCompany && (
        pointExpiration !== originalCompany.pointExpiration ||
        requirement !== originalCompany.requirement ||
        giftExpiration !== originalCompany.giftExpiration ||
        giftBasic?.label !== originalGiftBasic.label ||
        giftIntermediate?.label !== originalGiftIntermediate.label ||
        giftAdvanced?.label !== originalGiftAdvanced.label
      )
    );
  }, [pointExpiration, requirement, giftExpiration, giftBasic, giftIntermediate, giftAdvanced, originalCompany, originalGiftBasic, originalGiftIntermediate, originalGiftAdvanced]);

  const save = () => {
    if (errorPointExpiration) { toast.error(strings.LABEL_COMPANIES_POINT_EXPIRATION_ERROR); return; }
    if (errorGiftExpiration) { toast.error(strings.LABEL_COMPANIES_GIFT_EXPIRATION_ERROR); return; }
    if (errorRequirement) { toast.error(strings.LABEL_COMPANIES_REQUIREMENT_ERROR); return; }
    if (errorBasicGift) { toast.error("Regalo básico: solo letras, números y espacios"); return; }
    if (isPlus && errorIntermediateGift) { toast.error("Regalo intermedio: solo letras, números y espacios"); return; }
    if (isPlus && errorAdvancedGift) { toast.error("Regalo avanzado: solo letras, números y espacios"); return; }
    const companyId = company.id;
    dispatch(updateCompanyAction({ pointExpiration, giftExpiration, requirement }, companyId));
    if (giftBasic.id) dispatch(updateGiftAction(giftBasic, giftBasic.id));
    else dispatch(createGiftAction(giftBasic, companyId));
    if (company.strategy === "plus") {
      if (giftIntermediate.id) dispatch(updateGiftAction(giftIntermediate, giftIntermediate.id));
      else dispatch(createGiftAction(giftIntermediate, companyId));
      if (giftAdvanced.id) dispatch(updateGiftAction(giftAdvanced, giftAdvanced.id));
      else dispatch(createGiftAction(giftAdvanced, companyId));
    }
  };

  const cancel = () => dispatch(getCompanyAction(selectedCompany.id));

  if (!company) return null;

  const isPlus = company.strategy === "plus";

  return (
    <Box sx={{ width: "100%" }}>
      <ToastContainer position="bottom-right" />
      <div style={card}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: `1px solid ${BORDER}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              Estrategia
            </Typography>
            <span style={{
              background: isPlus ? 'rgba(91,191,181,0.15)' : 'rgba(59,149,232,0.15)',
              color: isPlus ? TEAL : BRAND,
              fontSize: 11, fontWeight: 700,
              padding: '3px 9px', borderRadius: 6, letterSpacing: 0.3,
            }}>
              {isPlus ? 'Fidelize+' : 'Standard'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={cancel}
              disabled={!dirty}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                color: dirty ? 'rgba(232,240,254,0.7)' : 'rgba(255,255,255,0.2)',
                fontSize: 13, fontWeight: 500,
                padding: '7px 14px',
                cursor: dirty ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <RestoreRoundedIcon style={{ fontSize: 15 }} />
              Cancelar
            </button>
            <button
              onClick={save}
              disabled={!valid || !dirty}
              style={{
                background: valid && dirty ? BRAND : 'rgba(59,149,232,0.2)',
                border: 'none',
                borderRadius: 8,
                color: valid && dirty ? '#fff' : 'rgba(255,255,255,0.3)',
                fontSize: 13, fontWeight: 600,
                padding: '7px 16px',
                cursor: valid && dirty ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: valid && dirty ? '0 4px 14px rgba(59,149,232,0.3)' : 'none',
                transition: 'all 0.15s',
              }}>
              <SaveRoundedIcon style={{ fontSize: 15 }} />
              Guardar
            </button>
          </div>
        </div>

        <div style={{ padding: '28px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>

          {/* Left — configuration */}
          <div>
            <div style={sectionTitle}>Configuración</div>

            <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_POINT_EXPIRATION}>
              <CustomTextField
                id="card-expiration"
                label={strings.LABEL_INPUT_CARD_EXPIRATION}
                value={pointExpiration}
                onChange={setPointExpiration}
                margin="none"
              />
            </FieldRow>

            <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_GIFT_EXPIRATION}>
              <CustomTextField
                id="gift-expiration"
                label={strings.LABEL_INPUT_GIFT_EXPIRATION}
                value={giftExpiration}
                onChange={setGiftExpiration}
                margin="none"
              />
            </FieldRow>

            <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_REQUIREMENT}>
              <CustomTextField
                id="requirement"
                label={strings.LABEL_INPUT_REQUIREMENT}
                value={requirement}
                onChange={setRequirement}
                margin="none"
              />
            </FieldRow>
          </div>

          {/* Right — gifts */}
          <div>
            <div style={sectionTitle}>Regalos</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <GiftCard
                tier="Básico"
                points={10}
                label={giftBasic.label}
                error={errorBasicGift}
                onChange={(value) => setGiftBasic({ ...giftBasic, label: value })}
                accent={BRAND}
              />
              {isPlus && (
                <>
                  <GiftCard
                    tier="Intermedio"
                    points={8}
                    label={giftIntermediate.label}
                    error={errorIntermediateGift}
                    onChange={(value) => setGiftIntermediate({ ...giftIntermediate, label: value })}
                    accent={TEAL}
                  />
                  <GiftCard
                    tier="Avanzado"
                    points={6}
                    label={giftAdvanced.label}
                    error={errorAdvancedGift}
                    onChange={(value) => setGiftAdvanced({ ...giftAdvanced, label: value })}
                    accent="#8b9fd4"
                  />
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </Box>
  );
};

export default Plus;
